/**
 * 🤝 PARTNER SERVICE - Integration with partner management APIs
 * Handles service partners (garages, GPS installers, insurance verifiers, documentation partners)
 */

// API Base URL
import { getApiBaseUrl } from '@/config/environment';

// Types for Partner API
export interface Partner {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  type: 'Garage & Inspection' | 'GPS Installation' | 'Insurance Verification' | 'Documentation';
  serviceType: 'Inspection' | 'GPS' | 'Documentation' | 'Insurance';
  completedJobs: number;
  trustRating: {
    overall: number;
    accuracy: number;
    timeliness: number;
    documentation: number;
  };
  avatar?: string;
  address?: string;
  city?: string;
  state?: string;
  status?: 'active' | 'inactive' | 'suspended';
  createdAt?: string;
  updatedAt?: string;
}

export interface Upload {
  id: string;
  partnerId: string;
  partner: string;
  type: 'Inspection' | 'GPS' | 'Documentation' | 'Insurance';
  document: string;
  documentUrl?: string;
  vehicleId?: string;
  vehicle: string;
  date: string;
  status: 'Verified' | 'Pending Review' | 'Rejected';
  notes?: string;
  reviewedBy?: string;
  reviewedAt?: string;
}

export interface Review {
  id: string;
  partnerId: string;
  partner: string;
  serviceType: 'Inspection' | 'GPS' | 'Documentation' | 'Insurance';
  vehicleId?: string;
  vehicle: string;
  date: string;
  rating: number;
  feedback: string;
  reviewerId?: string;
  reviewer?: string;
}

export interface PartnerStats {
  total: number;
  active: number;
  inactive: number;
  suspended: number;
  byServiceType: {
    Inspection: number;
    GPS: number;
    Documentation: number;
    Insurance: number;
  };
}

export interface UploadStats {
  inspectionReports: { count: number; description: string };
  gpsInstallation: { count: number; description: string };
  documentation: { count: number; description: string };
  insuranceVerification: { count: number; description: string };
}

export interface CreatePartnerData {
  name: string;
  email: string;
  phone: string;
  type: 'Garage & Inspection' | 'GPS Installation' | 'Insurance Verification' | 'Documentation';
  serviceType: 'Inspection' | 'GPS' | 'Documentation' | 'Insurance';
  address?: string;
  city?: string;
  state?: string;
}

export interface UpdatePartnerData extends Partial<CreatePartnerData> {
  status?: 'active' | 'inactive' | 'suspended';
}

export interface CreateUploadData {
  partnerId: string;
  type: 'Inspection' | 'GPS' | 'Documentation' | 'Insurance';
  document: string;
  documentUrl?: string;
  vehicleId: string;
  notes?: string;
}

export interface CreateReviewData {
  partnerId: string;
  serviceType: 'Inspection' | 'GPS' | 'Documentation' | 'Insurance';
  vehicleId: string;
  rating: number;
  feedback: string;
}

export interface PartnerResponse {
  success: boolean;
  data?: Partner;
  error?: string;
  message?: string;
}

export interface PartnersResponse {
  success: boolean;
  data?: {
    partners: Partner[];
    stats: PartnerStats;
  };
  error?: string;
  message?: string;
}

export interface UploadsResponse {
  success: boolean;
  data?: {
    uploads: Upload[];
    total: number;
  };
  error?: string;
  message?: string;
}

export interface ReviewsResponse {
  success: boolean;
  data?: {
    reviews: Review[];
    total: number;
  };
  error?: string;
  message?: string;
}

export interface UploadStatsResponse {
  success: boolean;
  data?: UploadStats;
  error?: string;
  message?: string;
}

 

// Initialize with defaults

// Load from localStorage or use defaults

export const partnerService = {

  /**
   * 📋 Get all partners with optional filters
   */
  async getPartners(filters?: {
    serviceType?: 'Inspection' | 'GPS' | 'Documentation' | 'Insurance';
    status?: 'active' | 'inactive' | 'suspended';
    minRating?: number;
    search?: string;
  }): Promise<PartnersResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (filters?.serviceType) {
        queryParams.append('serviceType', filters.serviceType);
      }
      if (filters?.status) {
        queryParams.append('status', filters.status);
      }
      if (filters?.minRating) {
        queryParams.append('minRating', filters.minRating.toString());
      }
      if (filters?.search) {
        queryParams.append('search', filters.search);
      }

      const url = `${getApiBaseUrl()}/api/v1/admin/partners${queryParams.toString() ? `?${queryParams}` : ''}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get Partners API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error fetching partners:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch partners'
      };
    }
  },

  /**
   * 🔍 Get a single partner by ID
   */
  async getPartner(id: string): Promise<PartnerResponse> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/partners/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get Partner API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error fetching partner:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch partner'
      };
    }
  },

  /**
   * ➕ Create a new partner
   */
  async createPartner(data: CreatePartnerData): Promise<PartnerResponse> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/partners`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Create Partner API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error creating partner:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create partner'
      };
    }
  },

  /**
   * ✏️ Update an existing partner
   */
  async updatePartner(id: string, data: UpdatePartnerData): Promise<PartnerResponse> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/partners/${id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Update Partner API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error updating partner:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update partner'
      };
    }
  },

  /**
   * 🗑️ Delete a partner
   */
  async deletePartner(id: string): Promise<PartnerResponse> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/partners/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Delete Partner API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error deleting partner:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete partner'
      };
    }
  },

  /**
   * 📤 Get uploads with optional filters
   */
  async getUploads(filters?: {
    partnerId?: string;
    type?: 'Inspection' | 'GPS' | 'Documentation' | 'Insurance';
    status?: 'Verified' | 'Pending Review' | 'Rejected';
    limit?: number;
  }): Promise<UploadsResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (filters?.partnerId) {
        queryParams.append('partnerId', filters.partnerId);
      }
      if (filters?.type) {
        queryParams.append('type', filters.type);
      }
      if (filters?.status) {
        queryParams.append('status', filters.status);
      }
      if (filters?.limit) {
        queryParams.append('limit', filters.limit.toString());
      }

      const url = `${getApiBaseUrl()}/api/v1/admin/partners/uploads${queryParams.toString() ? `?${queryParams}` : ''}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get Uploads API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error fetching uploads:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch uploads'
      };
    }
  },

  /**
   * ⬆️ Create a new upload
   */
  async createUpload(data: CreateUploadData): Promise<PartnerResponse> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/partners/uploads`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Create Upload API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error creating upload:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create upload'
      };
    }
  },

  /**
   * ✅ Update upload status (verify/reject)
   */
  async updateUploadStatus(
    uploadId: string,
    status: 'Verified' | 'Rejected',
    notes?: string
  ): Promise<PartnerResponse> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/partners/uploads/${uploadId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status, notes })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Update Upload Status API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error updating upload status:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update upload status'
      };
    }
  },

  /**
   * ⭐ Get reviews with optional filters
   */
  async getReviews(filters?: {
    partnerId?: string;
    serviceType?: 'Inspection' | 'GPS' | 'Documentation' | 'Insurance';
    minRating?: number;
    limit?: number;
  }): Promise<ReviewsResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (filters?.partnerId) {
        queryParams.append('partnerId', filters.partnerId);
      }
      if (filters?.serviceType) {
        queryParams.append('serviceType', filters.serviceType);
      }
      if (filters?.minRating) {
        queryParams.append('minRating', filters.minRating.toString());
      }
      if (filters?.limit) {
        queryParams.append('limit', filters.limit.toString());
      }

      const url = `${getApiBaseUrl()}/api/v1/admin/partners/reviews${queryParams.toString() ? `?${queryParams}` : ''}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get Reviews API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error fetching reviews:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch reviews'
      };
    }
  },

  /**
   * 📝 Create a new review
   */
  async createReview(data: CreateReviewData): Promise<PartnerResponse> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/partners/reviews`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Create Review API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error creating review:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create review'
      };
    }
  },

  /**
   * 📊 Get upload statistics
   */
  async getUploadStats(): Promise<UploadStatsResponse> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/partners/uploads/stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get Upload Stats API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error fetching upload stats:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch upload stats'
      };
    }
  },

  /**
   * 📥 Export partners to CSV
   */
  exportPartnersToCSV(partners: Partner[]): void {
    const csvContent = [
      ['Partner Name', 'Service Type', 'Email', 'Phone', 'Completed Jobs', 'Trust Rating', 'Status'].join(','),
      ...partners.map(partner => [
        partner.name,
        partner.serviceType,
        partner.email || '',
        partner.phone || '',
        partner.completedJobs,
        partner.trustRating.overall,
        partner.status || 'active'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `partners-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }
};
